/**
 * Standalone Hotels Portal - NFC Tap Handler
 * 
 * PUBLIC page - No login required.
 * Routes to appropriate flow based on card's engagement_type:
 * - guest_arrival: Redirects to 4-question check-in survey
 * - tap_engine: Shows review platform options
 * Full bilingual support (EN/FR).
 */
import React, { useState, useEffect } from 'react';
import { useParams, useSearchParams, useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { Star, Loader2, ExternalLink, AlertCircle, ClipboardCheck, Globe } from 'lucide-react';
import { Button } from '../../components/ui/button';
import { Card, CardContent } from '../../components/ui/card';
import { tLang } from '../../lib/langHelper';


const API_BASE = process.env.REACT_APP_BACKEND_URL || '';
const API_URL = `${API_BASE}/api/hotels-standalone`;

export default function StandaloneTapHandler() {
  const { t, i18n } = useTranslation();
  const { cardId } = useParams();
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const platform = searchParams.get('platform');
  const langParam = searchParams.get('lang');

  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [tapData, setTapData] = useState(null);

  // Force dark mode for standalone pages
  useEffect(() => {
    document.documentElement.classList.add('dark');
    return () => {
      const savedTheme = localStorage.getItem('yul_theme_preference');
      if (savedTheme !== 'dark') {
        document.documentElement.classList.remove('dark');
      }
    };
  }, []);

  // Set language from URL param if provided
  useEffect(() => {
    if (langParam && ['en', 'fr'].includes(langParam)) {
      i18n.changeLanguage(langParam);
    }
  }, [langParam, i18n]);

  const toggleLanguage = () => {
    const newLang = i18n.language === 'en' ? 'fr' : 'en';
    i18n.changeLanguage(newLang);
  };

  useEffect(() => {
    handleTap();
  }, [cardId]);

  const handleTap = async () => {
    try {
      const url = platform 
        ? `${API_URL}/tap/${cardId}?platform=${platform}`
        : `${API_URL}/tap/${cardId}`;

      const response = await fetch(url);
      const data = await response.json();

      if (!response.ok) {
        throw new Error(data.detail || t('standalone.tapHandler.cardNotFound'));
      }

      setTapData(data);
      setLoading(false);

      // Route based on engagement type
      if (data.engagement_type === 'guest_arrival' && data.flow === 'survey') {
        // Append language to redirect URL
        const redirectUrl = data.redirect_url + (data.redirect_url.includes('?') ? '&' : '?') + `lang=${i18n.language}`;
        navigate(redirectUrl, { replace: true });
      } else if (data.engagement_type === 'retailer_alert' && data.redirect_url) {
        navigate(data.redirect_url, { replace: true });
      } else if (data.redirect_url && !data.redirect_url.includes('review-landing')) {
        // IMMEDIATELY redirect to review platform - no delay!
        window.location.href = data.redirect_url;
        return; // Stop rendering
      }
    } catch (err) {
      setError(err.message);
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-blue-600 to-blue-800 flex items-center justify-center p-4">
        <Card className="w-full max-w-md bg-white/10 backdrop-blur border-white/20">
          <CardContent className="pt-6 text-center">
            <Loader2 className="w-12 h-12 text-white animate-spin mx-auto mb-4" />
            <p className="text-white text-lg">{t('standalone.tapHandler.loading')}</p>
          </CardContent>
        </Card>
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-red-600 to-red-800 flex items-center justify-center p-4">
        {/* Language Toggle */}
        <div className="absolute top-4 right-4">
          <LanguageSelector variant="compact" />
        </div>

        <Card className="w-full max-w-md bg-white/10 backdrop-blur border-white/20">
          <CardContent className="pt-6 text-center">
            <AlertCircle className="w-12 h-12 text-white mx-auto mb-4" />
            <p className="text-white text-lg font-medium mb-2">{t('standalone.tapHandler.cardNotFound')}</p>
            <p className="text-white/80 text-sm">{t('standalone.tapHandler.cardNotFoundDesc')}</p>
          </CardContent>
        </Card>
      </div>
    );
  }

  // Guest Arrival flow - should have redirected, but show fallback
  if (tapData?.engagement_type === 'guest_arrival') {
    return (
      <div className="min-h-screen bg-gradient-to-br from-green-600 to-green-800 flex items-center justify-center p-4">
        {/* Language Toggle */}
        <div className="absolute top-4 right-4">
          <LanguageSelector variant="compact" />
        </div>

        <Card className="w-full max-w-md bg-white shadow-2xl">
          <CardContent className="pt-8 pb-8 text-center">
            <div className="w-20 h-20 rounded-full bg-green-100 flex items-center justify-center mx-auto mb-6">
              <ClipboardCheck className="w-10 h-10 text-green-600" />
            </div>
            <h1 className="text-2xl font-bold text-gray-900 mb-2">
              {tLang(i18n.language, 'Welcome!', 'Bienvenue!', '¡Bienvenido!')}
            </h1>
            <p className="text-gray-600 mb-2">
              {tapData?.organization_name}
            </p>
            <p className="text-gray-500 text-sm mb-6">
              {tLang(i18n.language, 'Please complete our quick check-in survey.', 'Veuillez compléter notre court sondage d\'arrivée.', 'Please complete our quick check-in survey.')}
            </p>
            <Button
              className="w-full bg-green-600 hover:bg-green-700"
              onClick={() => {
                const redirectUrl = tapData.redirect_url + (tapData.redirect_url.includes('?') ? '&' : '?') + `lang=${i18n.language}`;
                navigate(redirectUrl);
              }}
            >
              <ClipboardCheck className="w-4 h-4 mr-2" />
              {tLang(i18n.language, 'Start Check-In Survey', 'Commencer le sondage', 'Iniciar encuesta de registro')}
            </Button>
          </CardContent>
        </Card>
      </div>
    );
  }

  // Tap Engine / Review flow
  return (
    <div className="min-h-screen bg-gradient-to-br from-blue-600 to-blue-800 flex items-center justify-center p-4">
      {/* Language Toggle */}
      <div className="absolute top-4 right-4">
        <LanguageSelector variant="compact" />
      </div>

      <Card className="w-full max-w-md bg-white shadow-2xl">
        <CardContent className="pt-8 pb-8 text-center">
          <div className="w-20 h-20 rounded-full bg-blue-100 flex items-center justify-center mx-auto mb-6">
            <Star className="w-10 h-10 text-blue-600" />
          </div>
          
          <h1 className="text-2xl font-bold text-gray-900 mb-2">
            {t('standalone.tapHandler.thankYou')}
          </h1>
          
          <p className="text-gray-600 mb-2">
            {tapData?.organization_name}
          </p>
          
          <p className="text-gray-500 text-sm mb-6">
            {tLang(i18n.language, 'We appreciate your business and would love to hear about your experience.', 'Nous apprécions votre visite et aimerions connaître votre expérience.', 'We appreciate your business and would love to hear about your experience.')}
          </p>

          {tapData?.redirect_url && !tapData.redirect_url.includes('review-landing') ? (
            <>
              <p className="text-sm text-gray-500 mb-4">
                {tLang(i18n.language, 'Redirecting you to leave a review...', 'Redirection vers la page d\'avis...', 'Redirecting you to leave a review...')}
              </p>
              <Button
                className="w-full bg-blue-600 hover:bg-blue-700"
                onClick={() => window.location.href = tapData.redirect_url}
              >
                <ExternalLink className="w-4 h-4 mr-2" />
                {tLang(i18n.language, 'Leave a Review on', 'Laisser un avis sur', 'Leave a Review on')} {tapData.platform?.charAt(0).toUpperCase() + tapData.platform?.slice(1)}
              </Button>
            </>
          ) : (
            <div className="space-y-3">
              <p className="text-sm text-gray-500 mb-4">
                {t('standalone.tapHandler.chooseReviewPlatform')}
              </p>
              <Button
                className="w-full bg-blue-600 hover:bg-blue-700"
                onClick={() => window.location.href = `${API_URL}/tap/${cardId}?platform=google`}
              >
                <Star className="w-4 h-4 mr-2" />
                {t('standalone.tapHandler.reviewOnGoogle')}
              </Button>
              <Button
                variant="outline"
                className="w-full"
                onClick={() => window.location.href = `${API_URL}/tap/${cardId}?platform=yelp`}
              >
                {t('standalone.tapHandler.reviewOnYelp')}
              </Button>
              <Button
                variant="outline"
                className="w-full"
                onClick={() => window.location.href = `${API_URL}/tap/${cardId}?platform=tripadvisor`}
              >
                {t('standalone.tapHandler.reviewOnTripAdvisor')}
              </Button>
            </div>
          )}

          <p className="text-xs text-gray-400 mt-6">
            {t('standalone.tapHandler.poweredBy')}
          </p>
        </CardContent>
      </Card>
    </div>
  );
}
